/**
 * 额度的展示层换算。
 *
 * v1.3.0 起契约只下发百分比（`Quota` = `{planId, scope, remainingPercent, unlimited, resetAt}`），
 * 前端拿不到任何绝对 token 量，也就**不再自己算百分比**——这个模块因此只剩三件事：
 * 把服务端给的百分比夹到进度条画得出来的范围、判断是否低于警告线、
 * 以及把 `scope` 翻译成人话（`scope` 与 `resetAt` 必须成对解释，否则会出现
 * "本月剩余 3%"配"今日 24:00 重置"这种自相矛盾的文案）。
 *
 * `QuotaBar`（顶部进度条）与额度页的计量条**共用**这里的函数：两处各判一次，
 * 迟早会出现"进度条说还剩 15%，明细说还剩 3%"的自相矛盾。
 *
 * 独立成纯函数模块的另一个原因：本项目的 vitest 只覆盖 src 目录下的 *.test.ts，
 * 组件不测，所以可测的规则必须留在 .ts 文件里。
 */

import type { Quota, QuotaScope } from '@spirit/contracts';

/** 低于此百分比即视为"额度紧张"，UI 转为警告色。 */
export const QUOTA_WARNING_THRESHOLD_PERCENT = 20;

/**
 * 额度的视图模型。
 *
 * 不限额度的分支**结构上就没有** `remainingPercent` 字段，而不是把它塞成 100：
 * 一个"看起来快满了"或"永远 100%"的进度条对一个不设闸的账号毫无意义，
 * 而且会让 `unlimited` 与"已用尽"在界面上长得一样。
 */
export type QuotaView =
  | {
      kind: 'limited';
      /** 服务端算好的 0-100 整数百分比；前端只做夹紧，不重算。 */
      remainingPercent: number;
      /** 这个百分比属于哪一维（日/月），与 `resetAt` 成对使用。 */
      scope: QuotaScope;
      /** `scope` 那一维的重置时刻。 */
      resetAt: string;
      /** 是否低于警告线。 */
      low: boolean;
    }
  | {
      kind: 'unlimited';
      scope: QuotaScope;
      resetAt: string;
    };

/**
 * 把契约的 `Quota` 摊平成视图模型。
 *
 * `remainingPercent` 由服务端算好（向下取整）并通过 schema 限定为 0-100 整数，
 * 这里仍然夹一次：它是进度条的宽度来源，一个越界值会直接把条画到容器外面。
 * 非有限值退成 0 而不是 `NaN`——`width: NaN%` 会静默变成无效样式，看起来就像"额度没了"。
 */
export function quotaViewOf(quota: Quota): QuotaView {
  if (quota.unlimited) {
    return { kind: 'unlimited', scope: quota.scope, resetAt: quota.resetAt };
  }

  const remainingPercent = clampPercent(quota.remainingPercent);
  return {
    kind: 'limited',
    remainingPercent,
    scope: quota.scope,
    resetAt: quota.resetAt,
    low: remainingPercent < QUOTA_WARNING_THRESHOLD_PERCENT,
  };
}

function clampPercent(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.max(0, Math.min(100, Math.round(value)));
}

/** 计量条的标题：说的是"被跟踪的那一维"（**今日**额度 / **本月**额度）。 */
export function quotaScopeTitleOf(scope: QuotaScope): string {
  return scope === 'daily' ? '今日额度' : '本月额度';
}

/** 百分比的限定语：让用户知道这个数字是哪一维算出来的。 */
export function quotaScopeLabelOf(scope: QuotaScope): string {
  return scope === 'daily' ? '按日额度计' : '按月额度计';
}

/** 两个维度都不设上限（内部试听档）时的说明；此时任何重置时间都没有意义。 */
export const UNLIMITED_QUOTA_HINT = '当前套餐不设额度上限';

/** 距离额度重置还有多久（毫秒），已过期或无法解析返回 0。 */
export function millisUntilReset(resetAt: string, now: number = Date.now()): number {
  const timestamp = new Date(resetAt).getTime();
  if (Number.isNaN(timestamp)) {
    return 0;
  }
  return Math.max(0, timestamp - now);
}

/** 重置倒计时文案：`3 小时 12 分后重置` / `12 分后重置` / `即将重置`。 */
export function formatResetCountdown(resetAt: string, now: number = Date.now()): string {
  const remaining = millisUntilReset(resetAt, now);
  if (remaining <= 0) {
    return '即将重置';
  }
  const totalMinutes = Math.floor(remaining / 60_000);
  if (totalMinutes < 1) {
    return '即将重置';
  }
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) {
    return `${minutes} 分后重置`;
  }
  if (minutes === 0) {
    return `${hours} 小时后重置`;
  }
  return `${hours} 小时 ${minutes} 分后重置`;
}
